import ReactDOM from 'react-dom/client';
import tailwindUrl from 'tailwindcss/tailwind.css?url';
import appCssUrl from 'app.css?url';
import fontsCssUrl from 'theme/fonts.css?url';
import webComponentHostCssUrl from 'theme/web-component-host.css?url';
import { userSession } from 'state/session';
import WebComponentRoot, { IWebComponentContext } from 'components/WebComponentRoot';
import { getFavicons } from 'theme/favicons';
import { TFaviconConfig } from 'theme/favicons/types';
import { webComponentAssetUrl } from './ui/stage';
import { detectTheme } from './ui/browserTheme';
import { applyUserTheme } from './ui/applyUserTheme';
import { brandSchema } from 'data/schema';

export type IWcOptions = {
  shadow?: boolean;
};

/**
 * A utility function to create a Web Component from a React Functional Component.
 * This function returns a class that extends `HTMLElement` and renders the given React component
 * inside the Web Component's shadow DOM or light DOM based on the provided options.
 *
 * @param Component - The React Functional Component to be rendered inside the Web Component.
 * @param options - Configuration options for the Web Component.
 * @param options.shadow - A boolean indicating whether to use shadow DOM. Defaults to `true`.
 *
 * @returns A class that extends `HTMLElement` and renders the React component.
 *
 * @example
 * ```typescript
 * const MyComponent: React.FC = () => <div>Hello, World!</div>;
 * customElements.define('my-component', wc(MyComponent));
 * ```
 */
export function wc(Component: React.FC, { shadow }: IWcOptions = { shadow: true }) {
  return class WebComponent extends HTMLElement {
    constructor() {
      super();
      if (!shadow) {
        return;
      }
      this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
      this.theme();
      const props = this.getPropsFromAttributes();
      const reactRootElement = shadow ? this.shadowRoot : this;
      const root = ReactDOM.createRoot(reactRootElement!);

      root.render(
        <WebComponentRoot
          {...props}
          rootElement={shadow ? (reactRootElement as ShadowRoot) : undefined}
          webComponent={this as unknown as IWebComponentContext['webComponent']}
          hasSlot={this.hasSlottedChildren()}
        >
          <Component />
        </WebComponentRoot>
      );

      if (shadow) {
        // move children to Shadow DOM to preserve Web Component styling
        this.shadowRoot?.append(...this.childNodes);
      }
    }

    private getPropsFromAttributes<T = Record<string, unknown>>(): T {
      const props: Record<string, string> = {};

      for (let index = 0; index < this.attributes.length; index++) {
        const attribute = this.attributes[index];
        props[normalizeAttribute(attribute.name)] = attribute.value;
      }

      return props as T;
    }

    _initialDisplay: string = '';

    hide() {
      this._initialDisplay = this.style.display;
      this.style.display = 'none';
    }

    show() {
      this.style.display = this._initialDisplay;

      const webComponentName = this.tagName.toLowerCase();
      this.classList.add(`${webComponentName}-ready`);
    }

    /**
     * Injects a stylesheet into the shadow DOM.
     * @returns A promise that resolves when the stylesheet is loaded.
     */
    private addStyleSheet(url: string) {
      // Check if the stylesheet is already loaded
      if (this.shadowRoot?.querySelector(`link[href="${url}"]`)) {
        return Promise.resolve();
      }

      const linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = url;
      this?.shadowRoot?.appendChild(linkElement);

      const loadingPromise = new Promise<void>((resolve, reject) => {
        linkElement.onload = () => {
          resolve();
        };
        linkElement.onerror = () => {
          reject();
        };
      });
      return loadingPromise;
    }

    theme() {
      // prevent FOUC while tailwind loads into the shadow DOM
      this.hide();

      // Inject the external stylesheet
      Promise.all([
        // tailwind
        this.addStyleSheet(webComponentAssetUrl(tailwindUrl)),
        // app css
        this.addStyleSheet(webComponentAssetUrl(appCssUrl)),
        // web component host css
        this.addStyleSheet(webComponentAssetUrl(webComponentHostCssUrl)),
      ]).then(() => {
        this.show();
      });

      // Check if the global style#e3d-theme exists and move it to the shadow root
      const globalStyle = document.querySelector('#e3d-theme');
      if (globalStyle) {
        this.shadowRoot?.appendChild(globalStyle.cloneNode(true));
      }

      this.applyBrandingCss();
      this.injectFonts();
    }

    private applyBrandingCss() {
      const brand = userSession().get('brand');

      if (brand) {
        this.classList.add(`brand-${brand}`);
        this.addFavicons(brand);
      }

      // Apply default customer class immediately. If it turns out the user is a dealer, we'll switch it after.
      this.classList.add('ex3d-customer');
      applyUserTheme();

      userSession().on('change', (keys, newValues) => {
        const isBrandBeingChanged = keys.includes('brand');
        if (isBrandBeingChanged) {
          const brandIndex = keys.indexOf('brand');
          const newBrand = newValues[brandIndex];
          const inactiveBrands = brandSchema.options.values().filter((b) => b !== newBrand);
          // We do not reliably know the exact brand that was previously applied, so we remove all irrelevant brand classes
          inactiveBrands.forEach((brand) => {
            this.classList.remove(`brand-${brand}`);
          });
          this.classList.add(`brand-${newBrand}`);
          this.addFavicons(newBrand);
        }
      });
    }

    private async addFavicons(brand: string) {
      try {
        const browserTheme = detectTheme();
        const { favicons } = await getFavicons(brand, browserTheme);

        const head = document.head || document.getElementsByTagName('head')[0] || document.createElement('head');
        if (!document.head) {
          document.documentElement.prepend(head);
        }

        favicons.forEach((favicon: TFaviconConfig) => {
          const searchAttributes = Object.keys(favicon).filter((key) => key !== 'href');

          const existingLink = Array.from(head.querySelectorAll('link')).find((existing) =>
            searchAttributes.every((key) => existing.getAttribute(key) === favicon[key as keyof TFaviconConfig])
          );

          if (existingLink) {
            head.removeChild(existingLink);
          }

          const link = document.createElement('link');
          Object.assign(link, favicon);
          head.appendChild(link);
        });
      } catch (error) {
        console.error('Failed to add favicons:', error);
      }
    }

    /**
     * Inject the fonts stylesheet into the document head so web component can use the fonts.
     */
    injectFonts() {
      const globalFontsStyle = document.querySelector('#e3d-fonts');
      if (globalFontsStyle) {
        // already added once, no need to re-apply
        return;
      }
      const fontsLinkElement = document.createElement('link');
      fontsLinkElement.id = 'e3d-fonts';
      fontsLinkElement.rel = 'stylesheet';
      fontsLinkElement.href = webComponentAssetUrl(fontsCssUrl);
      document.head.appendChild(fontsLinkElement);
    }

    private hasSlottedChildren() {
      return Array.from(this.childNodes).some(
        (node) => node.nodeType !== Node.TEXT_NODE || node.textContent?.trim() !== ''
      );
    }
  };
}

/**
 * Converts a kebab-case attribute name to camelCase.
 *
 * This function takes a string representing an attribute name in kebab-case
 * (e.g., "data-attribute-name") and converts it to camelCase (e.g., "dataAttributeName").
 *
 * @param attribute - The kebab-case attribute name to be normalized.
 * @returns The camelCase version of the attribute name.
 */
export function normalizeAttribute(attribute: string) {
  return attribute.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}
